/** The three kinds of asset a gallery holds, and that a digital product can be sold for. */
export type MediaType = 'photo' | 'video' | 'slideshow';

/**
 * Normalizes a media type off the wire: a product's `mediaType`, or an asset's `type` as the
 * AngularJS gallery spells it. Unrecognized reads as photo, and that is load-bearing: the API
 * emits `mediaType` only on SP-fulfilled digitals, and the rest sell photos.
 */
export const normalizeMediaType = (mediaType: Nullable<string> | undefined): MediaType =>
    mediaType === 'video' || mediaType === 'slideshow' ? mediaType : 'photo';

/**
 * True for a time-based medium. Takes anything `normalizeMediaType` accepts; only the API's
 * `event-*` spellings need `getAssetMediaType(asset)` first.
 */
export const isVideoOrSlideshow = (mediaType: Nullable<string> | undefined): boolean =>
    normalizeMediaType(mediaType) !== 'photo';

/**
 * Media type of an *asset*, whose API spells things differently from the price sheet and sometimes
 * types a video as `event-photo`, leaving only the mime type to say otherwise.
 */
export const getAssetMediaType = (photo: {
    type?: string;
    mimeType?: Nullable<string>;
}): MediaType => {
    const { type, mimeType } = photo;

    if (type === 'video' || type === 'event-video') {
        return 'video';
    }

    if (type === 'slideshow' || type === 'event-slideshow' || type === 'event-album-slideshow') {
        return 'slideshow';
    }

    if (typeof mimeType === 'string' && mimeType.startsWith('video/')) {
        return 'video';
    }

    return 'photo';
};

const PHOTO_ONLY: readonly MediaType[] = ['photo'];

/**
 * Every media type a product sells. A package answers for its children — its own wrapper never
 * carries a `mediaType` — and both no item and an empty package answer photo.
 */
export const getProductMediaTypes = (
    item: Nullable<SpApi.Client.IPriceSheetItem> | undefined
): readonly MediaType[] => {
    if (!item) {
        return PHOTO_ONLY;
    }

    if (item.type !== 'price-sheet-item-package') {
        return [normalizeMediaType(item.mediaType)];
    }

    const { items = [] } = item;

    return items.length === 0
        ? PHOTO_ONLY
        : items.map(({ mediaType }) => normalizeMediaType(mediaType));
};

/**
 * The one media type a product sells, for copy that has to name a kind of asset. A mixed package
 * has no single answer and reports photo — use `isAssetSellableAsProduct` to drive behaviour.
 */
export const getProductDisplayMediaType = (
    item: Nullable<SpApi.Client.IPriceSheetItem> | undefined
): MediaType => {
    const [firstMediaType, ...rest] = getProductMediaTypes(item);

    return rest.every((mediaType) => mediaType === firstMediaType) ? firstMediaType : 'photo';
};

/**
 * Whether an asset of this media type can be sold as this product. A package can take the asset if
 * any one of its children sells it.
 */
export const isAssetSellableAsProduct = (
    assetMediaType: MediaType,
    item: Nullable<SpApi.Client.IPriceSheetItem> | undefined
): boolean => getProductMediaTypes(item).includes(assetMediaType);
